/**
 * Static explainer content (handoffV5 §12.3). The sanctioned home for the
 * explanation that §11 bans from the working surface. Fixed copy — does not
 * change with the data. Keyed by view: an engine id ("engine:3"), a sub-tab
 * id ("capacity_utilization"), or "engine-room". Lookup falls back
 * sub-tab → engine → generic.
 */
export interface Explainer {
  purpose: string;       // what this view is for
  dataMeaning: string;   // how to read the metrics
  owner: string;         // who owns it
  howToAct: string;      // dispositions available / how to act
}

export const EXPLAINERS: Record<string, Explainer> = {
  'engine-room': {
    purpose: 'The control surface for the whole company, laid out as the value chain a can moves through: Innovation → Sourcing → Manufacturing → Distribution → Go-to-Market, with Simulation and Corp-Dev running across.',
    dataMeaning: 'Each tile shows that engine\'s headline number, owner, and how many of its plays defend the 99¢ moat (◆ Core) vs. are commodity hygiene (○). The Alert band on top ranks what needs attention right now.',
    owner: 'CEO / strategy office.',
    howToAct: 'Click an alert to jump to what resolves it, or open any engine to work inside it.',
  },
  'engine:1': {
    purpose: 'Innovation turns consumer signals into fillable products that hold the 99¢ economics.',
    dataMeaning: 'Concept survival, cost-to-99¢ gaps, and label/IP clearance. Green means a concept can move to fill; amber means a cost or claims gate is open.',
    owner: 'Growth / R&D.',
    howToAct: 'Run the Rapid Formulation studio, score adjacencies, and clear claims before a launch commitment.',
  },
  'engine:2': {
    purpose: 'Sourcing defends the 99¢ promise on the input side — commodity cost and supply resilience.',
    dataMeaning: 'Profit-at-Risk shows the input-cost downside band; hedge coverage and single-supplier share show how exposed that downside is.',
    owner: 'CFO / Procurement.',
    howToAct: 'Read the latest Profit-at-Risk run, then act on hedge coverage and dual-sourcing where a rule fires.',
  },
  'engine:3': {
    purpose: 'Manufacturing is the COGS lever — filling lines efficiently is how the 99¢ math stays intact.',
    dataMeaning: 'OEE, capacity utilization, yield, and quality. Utilization below 80% or yield below 94% is money left on the floor.',
    owner: 'COO / Plant Ops.',
    howToAct: 'Re-slot under-run lines, schedule predictive maintenance before failures, and trace any quality flag to its batch.',
  },
  'engine:4': {
    purpose: 'Distribution gets the can to the shelf at a cost that preserves the margin — bodega density is the moat.',
    dataMeaning: 'Cost-to-serve by channel, route drop-density, freight density, and fill rate. Low fill rate or thin freight density erodes the economics.',
    owner: 'Sales / Logistics.',
    howToAct: 'Optimize routes, lift freight density, and clear replenishment alerts before they become out-of-stocks.',
  },
  'engine:5': {
    purpose: 'Go-to-Market grows demand through earned media and fandom — near-zero paid ad spend is the moat.',
    dataMeaning: 'Fandom heat, earned reach, Club ’Zona economics, and the ad-spend guardrail. Paid spend drifting up is the alarm.',
    owner: 'Brand / DTC / Sales.',
    howToAct: 'Turn hot signals into SKUs, hold the pre-priced 99¢ shelf, and keep paid spend inside the guardrail.',
  },
  'engine:6': {
    purpose: 'Simulation stress-tests strategy before committing real money — five models that all produce one decision.',
    dataMeaning: 'Each run produces a Move Brief: the recommended move, the evidence with confidence bands, the trade-offs (including blocked options), and the action path.',
    owner: 'Strategy / CEO staff.',
    howToAct: 'Run a simulation, read its Move Brief, and click a disposition to log the decision to institutional memory.',
  },
  'engine:7': {
    purpose: 'Corporate Development screens capacity and freight acquisitions — never brand acquisitions.',
    dataMeaning: 'Targets are scored on capacity gain, freight saving, and fit. Every target carries a no-brand-acquisition flag.',
    owner: 'Corp Dev / Supply Chain.',
    howToAct: 'Screen targets, run diligence and freight-redundancy models, and structure partnerships with a call option.',
  },
};

const GENERIC: Explainer = {
  purpose: 'A working intervention inside this engine — its own inputs, decision rules, and KPI.',
  dataMeaning: 'The headline at the top reads the live numbers in plain language; a recommended action appears only when a decision rule fires.',
  owner: 'See the KPI / Owner panel above.',
  howToAct: 'Work inside the view; where a rule fires, a recommended action with a disposition appears.',
};

export function resolveExplainer(view: string, engineId?: number): Explainer {
  return EXPLAINERS[view] ?? (engineId ? EXPLAINERS[`engine:${engineId}`] : undefined) ?? GENERIC;
}
